import { createUseStyles } from 'react-jss';
import { ControlEndIconColors } from '../../../../foundation/colors/controlEndIconColors/controlEndIconColors/index.js';
import { ControlStartIconColors } from '../../../../foundation/colors/controlStartIconColors/controlStartIconColors/index.js';
import { Easing } from '../../../../foundation/easing/easing/index.js';
import { Timings } from '../../../../foundation/timings/timings/index.js';
import { FontWeights } from '../../../../foundation/fonts/fonts/index.js';
import { CornerRadius } from '../../../../foundation/cornerRadius/cornerRadius/index.js';
import { BreakPointsMedia } from '../../../../foundation/responsive/responsive/index.js';
import { isRightToLeft, isLeftToRight } from '../../../../foundation/styleHelpers/rtl/rtl/index.js';
import { States } from '../../../../foundation/states/states/index.js';
import { SuccessColors } from '../../../../foundation/colors/successColors/successColors/index.js';
import { WarningColors } from '../../../../foundation/colors/warningColors/warningColors/index.js';
import { ErrorColors } from '../../../../foundation/colors/errorColors/errorColors/index.js';
import { ControlBackgroundColors } from '../../../../foundation/colors/controlBackgroundColors/controlBackgroundColors/index.js';
import { ControlValueColors } from '../../../../foundation/colors/controlValueColors/controlValueColors/index.js';
import { ControlBorderColors } from '../../../../foundation/colors/controlBorderColors/controlBorderColors/index.js';
import { placeholders } from '../../../../foundation/styleHelpers/placeholders/placeholders/index.js';
import { canTouch, canHover } from '../../../../foundation/styleHelpers/hoverTouch/hoverTouch/index.js';

const useLayoutStyles = createUseStyles({
    root: {
        width: '100%',
        height: ({ sizes }) => sizes?.Default.value?.height,
        position: ({ startIcon, endIcon }) => (startIcon || endIcon) && 'relative',
        [BreakPointsMedia.XXL]: {
            height: ({ sizes }) => sizes?.XXL.value?.height,
        },
        [BreakPointsMedia.XL]: {
            height: ({ sizes }) => sizes?.XL.value?.height,
        },
        [BreakPointsMedia.LG]: {
            height: ({ sizes }) => sizes?.LG.value?.height,
        },
        [BreakPointsMedia.MD]: {
            height: ({ sizes }) => sizes?.MD.value?.height,
        },
        [BreakPointsMedia.SM]: {
            height: ({ sizes }) => sizes?.SM.value?.height,
        },
        [BreakPointsMedia.XS]: {
            height: ({ sizes }) => sizes?.XS.value?.height,
        },
        [BreakPointsMedia.XXS]: {
            height: ({ sizes }) => sizes?.XXS.value?.height,
        },
    },
    placeholderIcon: {
        display: 'inline-block',
        verticalAlign: 'top',
        width: ({ sizes }) => sizes?.Default.value?.iconSize,
        height: ({ sizes }) => sizes?.Default.value?.iconSize,
        position: 'absolute',
        top: 0,
        bottom: 0,
        margin: 'auto 0',
        pointerEvents: 'none',
        [isLeftToRight]: {
            left: ({ sizes }) => sizes.Default.value?.paddingHorizontal,
        },
        [isRightToLeft]: {
            right: ({ sizes }) => sizes.Default.value?.paddingHorizontal,
        },
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes?.XXL.value?.iconSize,
            height: ({ sizes }) => sizes?.XXL.value?.iconSize,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes?.XL.value?.iconSize,
            height: ({ sizes }) => sizes?.XL.value?.iconSize,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes?.LG.value?.iconSize,
            height: ({ sizes }) => sizes?.LG.value?.iconSize,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes?.MD.value?.iconSize,
            height: ({ sizes }) => sizes?.MD.value?.iconSize,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes?.SM.value?.iconSize,
            height: ({ sizes }) => sizes?.SM.value?.iconSize,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes?.XS.value?.iconSize,
            height: ({ sizes }) => sizes?.XS.value?.iconSize,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes?.XXS.value?.iconSize,
            height: ({ sizes }) => sizes?.XXS.value?.iconSize,
        },
    },
    control: {
        display: 'block',
        width: '100%',
        height: '100%',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: CornerRadius.r8,
        fontWeight: FontWeights.medium,
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '&[type=number]': {
            '-moz-appearance': 'textfield',
        },
        textAlign: ({ isCentered }) => isCentered && 'center',
        fontSize: ({ sizes }) => sizes?.Default.value?.fontSize,
        lineHeight: ({ sizes }) => sizes?.Default.value?.lineHeight,
        transitionProperty: 'background-color, border, color',
        transitionDuration: Timings.default,
        transitionTimingFunction: Easing.ease,
        [BreakPointsMedia.XXL]: {
            fontSize: ({ sizes }) => sizes?.XXL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XXL.value?.lineHeight,
        },
        [BreakPointsMedia.XL]: {
            fontSize: ({ sizes }) => sizes?.XL.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XL.value?.lineHeight,
        },
        [BreakPointsMedia.LG]: {
            fontSize: ({ sizes }) => sizes?.LG.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.LG.value?.lineHeight,
        },
        [BreakPointsMedia.MD]: {
            fontSize: ({ sizes }) => sizes?.MD.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.MD.value?.lineHeight,
        },
        [BreakPointsMedia.SM]: {
            fontSize: ({ sizes }) => sizes?.SM.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.SM.value?.lineHeight,
        },
        [BreakPointsMedia.XS]: {
            fontSize: ({ sizes }) => sizes?.XS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XS.value?.lineHeight,
        },
        [BreakPointsMedia.XXS]: {
            fontSize: ({ sizes }) => sizes?.XXS.value?.fontSize,
            lineHeight: ({ sizes }) => sizes?.XXS.value?.lineHeight,
        },
    },
    controlDefault: {
        padding: ({ sizes }) => `0 ${sizes?.Default.value?.paddingHorizontal}`,
        [BreakPointsMedia.XXL]: {
            padding: ({ sizes }) => `0 ${sizes?.XXL.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XL]: {
            padding: ({ sizes }) => `0 ${sizes?.XL.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.LG]: {
            padding: ({ sizes }) => `0 ${sizes?.LG.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.MD]: {
            padding: ({ sizes }) => `0 ${sizes?.MD.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.SM]: {
            padding: ({ sizes }) => `0 ${sizes?.SM.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XS]: {
            padding: ({ sizes }) => `0 ${sizes?.XS.value?.paddingHorizontal}`,
        },
        [BreakPointsMedia.XXS]: {
            padding: ({ sizes }) => `0 ${sizes?.XXS.value?.paddingHorizontal}`,
        },
    },
    controlPlaceholder: {
        paddingInlineStart: ({ sizes }) => `calc(${sizes?.Default.value?.paddingHorizontal} + ${sizes?.Default.value?.iconSize}px + ${sizes?.Default.value?.gap})`,
        [BreakPointsMedia.XXL]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.XXL.value?.paddingHorizontal} + ${sizes?.XXL.value?.iconSize}px + ${sizes?.XXL.value?.gap})`,
        },
        [BreakPointsMedia.XL]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.XL.value?.paddingHorizontal} + ${sizes?.XL.value?.iconSize}px + ${sizes?.XL.value?.gap})`,
        },
        [BreakPointsMedia.LG]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.LG.value?.paddingHorizontal} + ${sizes?.LG.value?.iconSize}px + ${sizes?.LG.value?.gap})`,
        },
        [BreakPointsMedia.MD]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.MD.value?.paddingHorizontal} + ${sizes?.MD.value?.iconSize}px + ${sizes?.MD.value?.gap})`,
        },
        [BreakPointsMedia.SM]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.SM.value?.paddingHorizontal} + ${sizes?.SM.value?.iconSize}px + ${sizes?.SM.value?.gap})`,
        },
        [BreakPointsMedia.XS]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.XS.value?.paddingHorizontal} + ${sizes?.XS.value?.iconSize}px + ${sizes?.XS.value?.gap})`,
        },
        [BreakPointsMedia.XXS]: {
            paddingInlineStart: ({ sizes }) => `calc(${sizes?.XXS.value?.paddingHorizontal} + ${sizes?.XXS.value?.iconSize}px + ${sizes?.XXS.value?.gap})`,
        },
    },
    controlButton: {
        paddingInlineEnd: ({ sizes }) => `calc(${sizes?.Default.value?.paddingHorizontal} + ${sizes?.Default.value?.iconSize}px + ${sizes?.Default.value?.gap})`,
        [BreakPointsMedia.XXL]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.XXL.value?.paddingHorizontal} + ${sizes?.XXL.value?.iconSize}px + ${sizes?.XXL.value?.gap})`,
        },
        [BreakPointsMedia.XL]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.XL.value?.paddingHorizontal} + ${sizes?.XL.value?.iconSize}px + ${sizes?.XL.value?.gap})`,
        },
        [BreakPointsMedia.LG]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.LG.value?.paddingHorizontal} + ${sizes?.LG.value?.iconSize}px + ${sizes?.LG.value?.gap})`,
        },
        [BreakPointsMedia.MD]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.MD.value?.paddingHorizontal} + ${sizes?.MD.value?.iconSize}px + ${sizes?.MD.value?.gap})`,
        },
        [BreakPointsMedia.SM]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.SM.value?.paddingHorizontal} + ${sizes?.SM.value?.iconSize}px + ${sizes?.SM.value?.gap})`,
        },
        [BreakPointsMedia.XS]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.XS.value?.paddingHorizontal} + ${sizes?.XS.value?.iconSize}px + ${sizes?.XS.value?.gap})`,
        },
        [BreakPointsMedia.XXS]: {
            paddingInlineEnd: ({ sizes }) => `calc(${sizes?.XXS.value?.paddingHorizontal} + ${sizes?.XXS.value?.iconSize}px + ${sizes?.XXS.value?.gap})`,
        },
    },
    button: {
        display: 'inline-block',
        verticalAlign: 'top',
        width: ({ sizes }) => sizes?.Default.value?.iconSize,
        height: ({ sizes }) => sizes?.Default.value?.iconSize,
        position: 'absolute',
        top: 0,
        bottom: 0,
        margin: 'auto 0',
        cursor: 'pointer',
        [isLeftToRight]: {
            right: ({ sizes }) => sizes.Default.value?.paddingHorizontal,
        },
        [isRightToLeft]: {
            left: ({ sizes }) => sizes.Default.value?.paddingHorizontal,
        },
        [BreakPointsMedia.XXL]: {
            width: ({ sizes }) => sizes?.XXL.value?.iconSize,
            height: ({ sizes }) => sizes?.XXL.value?.iconSize,
        },
        [BreakPointsMedia.XL]: {
            width: ({ sizes }) => sizes?.XL.value?.iconSize,
            height: ({ sizes }) => sizes?.XL.value?.iconSize,
        },
        [BreakPointsMedia.LG]: {
            width: ({ sizes }) => sizes?.LG.value?.iconSize,
            height: ({ sizes }) => sizes?.LG.value?.iconSize,
        },
        [BreakPointsMedia.MD]: {
            width: ({ sizes }) => sizes?.MD.value?.iconSize,
            height: ({ sizes }) => sizes?.MD.value?.iconSize,
        },
        [BreakPointsMedia.SM]: {
            width: ({ sizes }) => sizes?.SM.value?.iconSize,
            height: ({ sizes }) => sizes?.SM.value?.iconSize,
        },
        [BreakPointsMedia.XS]: {
            width: ({ sizes }) => sizes?.XS.value?.iconSize,
            height: ({ sizes }) => sizes?.XS.value?.iconSize,
        },
        [BreakPointsMedia.XXS]: {
            width: ({ sizes }) => sizes?.XXS.value?.iconSize,
            height: ({ sizes }) => sizes?.XXS.value?.iconSize,
        },
    },
    buttonIcon: {},
}, {
    name: 'text-field',
});
const useSkinStyles = createUseStyles({
    default: {
        ...placeholders,
        borderColor: ControlBorderColors.default,
        color: ControlValueColors.default,
        border: ({ isWithoutBorders }) => isWithoutBorders && 'none',
        backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.default),
    },
    placeholderIcon: {
        color: ControlStartIconColors.default,
        '& > path': {
            fill: 'currentColor',
        },
    },
    interactive: {
        [canHover]: {
            '&:hover': {
                ...placeholders,
                borderColor: ControlBorderColors.action,
                color: ControlValueColors.action,
                backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.action),
                '& + $placeholderIcon': {
                    color: ControlStartIconColors.action,
                },
            },
        },
        [canTouch]: {
            '&:active': {
                ...placeholders,
                borderColor: ControlBorderColors.action,
                color: ControlValueColors.action,
                backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.action),
                '& + $placeholderIcon': {
                    color: ControlStartIconColors.action,
                },
            },
        },
        '&:focus': {
            borderColor: ControlBorderColors.active,
            color: ControlValueColors.active,
            backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.active),
        },
    },
    filled: {
        color: ControlValueColors.active,
        backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.active),
        borderColor: ControlBorderColors.active,
        '& + $placeholderIcon': {
            color: ControlStartIconColors.active,
        },
        [canHover]: {
            '&:hover': {
                borderColor: ControlBorderColors.activeAction,
                color: ControlValueColors.activeAction,
                backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.activeAction),
                '& + $placeholderIcon': {
                    color: ControlStartIconColors.activeAction,
                },
            },
        },
        [canTouch]: {
            '&:active': {
                borderColor: ControlBorderColors.activeAction,
                color: ControlValueColors.activeAction,
                backgroundColor: ({ isWithoutBackground }) => (isWithoutBackground ? 'transparent' : ControlBackgroundColors.activeAction),
            },
            '& + $placeholderIcon': {
                color: ControlStartIconColors.activeAction,
            },
        },
    },
    error: {
        borderColor: ErrorColors.base.default,
        [canHover]: {
            '&:hover': {
                borderColor: ErrorColors.base.action,
            },
        },
        [canTouch]: {
            '&:active': {
                borderColor: ErrorColors.base.action,
            },
        },
    },
    warning: {
        borderColor: WarningColors.base.default,
        [canHover]: {
            '&:hover': {
                borderColor: WarningColors.base.action,
            },
        },
        [canTouch]: {
            '&:active': {
                borderColor: WarningColors.base.action,
            },
        },
    },
    success: {
        borderColor: SuccessColors.base.default,
        [canHover]: {
            '&:hover': {
                borderColor: SuccessColors.base.action,
            },
        },
        [canTouch]: {
            '&:active': {
                borderColor: SuccessColors.base.action,
            },
        },
    },
    disabled: {
        opacity: States.disabled,
    },
    button: {
        border: 'none',
        background: 'none',
        color: ControlEndIconColors.default,
        transition: `color ${Timings.default} ${Easing.ease}`,
        [canHover]: {
            '&:hover': {
                color: ControlEndIconColors.action,
            },
        },
        [canTouch]: {
            '&:active': {
                color: ControlEndIconColors.action,
            },
        },
    },
    buttonIcon: {
        display: 'block',
        width: '100%',
        height: '100%',
        '& > path': {
            fill: 'currentColor',
        },
    },
}, {
    name: 'text-field',
});

export { useLayoutStyles, useSkinStyles };
